import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Plus, Pencil, Trash2, Building2, X, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contact_person: '', phone: '', email: '', notes: '', is_active: true, service_ids: [], city_ids: [] });

  const loadData = () => {
    setLoading(true);
    Promise.all([
      adminAPI.getCompanies(),
      adminAPI.getServices(),
      adminAPI.getCities()
    ]).then(([cRes, sRes, ctRes]) => {
      setCompanies(cRes.data);
      setServices(sRes.data);
      setCities(ctRes.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setForm({ name: '', contact_person: '', phone: '', email: '', notes: '', is_active: true, service_ids: [], city_ids: [] });
    setEditing(null);
    setShowForm(false);
  };

  const startEdit = (company) => {
    setForm({
      name: company.name, contact_person: company.contact_person, phone: company.phone,
      email: company.email, notes: company.notes, is_active: company.is_active,
      service_ids: (company.services || []).map(s => s.service_id),
      city_ids: (company.cities || []).map(c => c.city_id)
    });
    setEditing(company.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Firma adi zorunlu'); return; }
    try {
      if (editing) {
        await adminAPI.updateCompany(editing, form);
        toast.success('Firma guncellendi');
      } else {
        await adminAPI.createCompany(form);
        toast.success('Firma eklendi');
      }
      resetForm();
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata olustu'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu firmayi silmek istediginize emin misiniz?')) return;
    try {
      await adminAPI.deleteCompany(id);
      toast.success('Firma silindi');
      loadData();
    } catch { toast.error('Silinemedi'); }
  };

  const toggleServiceId = (sid) => {
    setForm(f => ({
      ...f,
      service_ids: f.service_ids.includes(sid) ? f.service_ids.filter(x => x !== sid) : [...f.service_ids, sid]
    }));
  };

  const toggleCityId = (cid) => {
    setForm(f => ({
      ...f,
      city_ids: f.city_ids.includes(cid) ? f.city_ids.filter(x => x !== cid) : [...f.city_ids, cid]
    }));
  };

  return (
    <div data-testid="admin-companies">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Anlasmali Firmalar</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-company-btn">
          <Plus className="h-4 w-4 mr-2" /> Firma Ekle
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editing ? 'Firma Duzenle' : 'Yeni Firma'}</CardTitle>
            <button onClick={resetForm}><X className="h-5 w-5 text-muted-foreground" /></button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Firma Adi *</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="company-name" /></div>
              <div><Label>Yetkili Kisi</Label><Input value={form.contact_person} onChange={e => setForm(f => ({...f, contact_person: e.target.value}))} /></div>
              <div><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} /></div>
              <div><Label>E-posta</Label><Input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} /></div>
            </div>
            <div><Label>Notlar</Label><textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({...f, is_active: v}))} />
              <Label>Aktif</Label>
            </div>
            <div>
              <Label className="mb-2 block">Hizmetler</Label>
              <div className="flex flex-wrap gap-2">
                {services.map(s => (
                  <Badge key={s.id} variant={form.service_ids.includes(s.id) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleServiceId(s.id)}>
                    {s.name}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Sehirler</Label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {cities.map(c => (
                  <Badge key={c.id} variant={form.city_ids.includes(c.id) ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => toggleCityId(c.id)}>
                    {c.name}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} data-testid="company-submit">{editing ? 'Guncelle' : 'Kaydet'}</Button>
              <Button variant="outline" onClick={resetForm}>Iptal</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Companies List */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : companies.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Henuz firma eklenmemis</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {companies.map(c => (
            <Card key={c.id} data-testid={`company-${c.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="h-5 w-5 text-[hsl(221,83%,22%)]" />
                      <h3 className="font-semibold">{c.name}</h3>
                      <Badge variant={c.is_active ? 'default' : 'secondary'}>{c.is_active ? 'Aktif' : 'Pasif'}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-0.5">
                      {c.contact_person && <p>Yetkili: {c.contact_person}</p>}
                      {c.phone && <p>Tel: {c.phone}</p>}
                      {c.email && <p>E-posta: {c.email}</p>}
                    </div>
                    {(c.services || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {c.services.map((s, i) => <Badge key={i} variant="outline" className="text-xs">{services.find(sv => sv.id === s.service_id)?.name || s.service_id}</Badge>)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(c)} data-testid={`edit-company-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
